import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Cookie } from "lucide-react";
import { getConsent, setConsent } from "@/lib/consent";

export function CookieNotice() {
    const [show, setShow] = useState(false);
    const [prefs, setPrefs] = useState(false);
    const [functional, setFunctional] = useState(true);

    useEffect(() => {
        const c = getConsent();
        if (!c) setShow(true);
        else setFunctional(!!c.functional);
        const open = () => {
            const cur = getConsent();
            setFunctional(cur ? !!cur.functional : true);
            setPrefs(true);
        };
        window.addEventListener("pe-open-cookie-prefs", open);
        return () => window.removeEventListener("pe-open-cookie-prefs", open);
    }, []);

    const acceptAll = () => { setConsent(true); setShow(false); setPrefs(false); };
    const rejectNonEssential = () => { setConsent(false); setShow(false); setPrefs(false); };
    const savePrefs = () => { setConsent(functional); setShow(false); setPrefs(false); };

    return (
        <>
            {show && (
                <div className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:max-w-md z-50 bg-white border border-border rounded-xl shadow-lg p-4 pe-fade-up" data-testid="cookie-notice">
                    <div className="flex items-start gap-3">
                        <Cookie className="h-5 w-5 text-brand-teal mt-0.5 shrink-0" />
                        <div className="text-sm text-muted-foreground">
                            <p className="font-medium text-brand-emerald">We value your privacy</p>
                            <p className="mt-1">
                                We use strictly necessary cookies to run ProcureEd and keep you signed in. With your consent
                                we also use functional cookies to remember preferences such as your school route. We do not use
                                advertising or tracking cookies. Read more in our{" "}
                                <a href="/privacy" className="text-brand-teal underline underline-offset-2" data-testid="cookie-policy-link">Privacy &amp; Cookie Policy</a>.
                            </p>
                            <div className="mt-3 flex flex-wrap gap-2">
                                <Button size="sm" className="bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" onClick={acceptAll} data-testid="cookie-accept-all">Accept all</Button>
                                <Button size="sm" variant="outline" className="rounded-lg" onClick={rejectNonEssential} data-testid="cookie-reject">Reject non-essential</Button>
                                <Button size="sm" variant="ghost" className="rounded-lg" onClick={() => setPrefs(true)} data-testid="cookie-preferences">Preferences</Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Dialog open={prefs} onOpenChange={setPrefs}>
                <DialogContent data-testid="cookie-prefs-dialog">
                    <DialogHeader><DialogTitle>Cookie preferences</DialogTitle></DialogHeader>
                    <div className="space-y-4 text-sm">
                        <p className="text-muted-foreground">
                            Manage how ProcureEd uses cookies and local storage on this device. You can change these choices
                            at any time from the “Cookie preferences” link in the footer.
                        </p>
                        <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-3">
                            <div>
                                <div className="font-medium text-brand-emerald">Strictly necessary</div>
                                <p className="text-muted-foreground mt-0.5">Required to sign in, keep your session secure and remember your consent. Always on.</p>
                            </div>
                            <Switch checked disabled data-testid="consent-essential" />
                        </div>
                        <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-3">
                            <div>
                                <div className="font-medium text-brand-emerald">Functional</div>
                                <p className="text-muted-foreground mt-0.5">Remembers preferences such as your school route so you don't have to retype it.</p>
                            </div>
                            <Switch checked={functional} onCheckedChange={setFunctional} data-testid="consent-functional" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={rejectNonEssential} data-testid="cookie-prefs-reject">Reject non-essential</Button>
                        <Button className="bg-brand-emerald hover:bg-brand-emeraldhover" onClick={savePrefs} data-testid="cookie-prefs-save">Save choices</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
